import {
  Button,
  Container,
  Icon,
  Loader,
  Modal,
  Select,
  Spacer,
  SvgAdd,
  SvgFolder,
  SvgLock,
  SvgUser,
  SvgUsers,
  Text,
  TextButton,
} from '@loomhq/lens';
import anime from 'animejs';
import classnames from 'classnames';
import _every from 'lodash/every';
import _isEmpty from 'lodash/isEmpty';
import Mousetrap from 'mousetrap';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  clearMoveModalFolders,
  createNewFolderHide,
  createNewFolderShow,
  createNewFolderUpdateName,
  updateFolderToMoveTo,
  updateShowMoveModal,
} from 'actions/moveModal';
import { KEY_ENTER, KEY_ESCAPE } from 'constants/keyCodes';
import { ESCAPE } from 'constants/mousetrap';
import { FOLDER, VIDEO } from 'constants/videosDashboard';
import {
  createFolderCreator,
  fetchSubFoldersCreator,
} from 'creators/moveModal';
import { moveVideoCreator } from 'creators/shareVideo';
import {
  bulkMoveFoldersVideosCreator,
  updatePastForMove,
} from 'creators/videosDashboard';

import { hasScope } from 'selectors/scopes';

import TopLevelGridCell from './top-level-grid-cell';
import {
  PERSONAL_LIBRARY_WRITE,
  SHARED_LIBRARY_WRITE,
  TEAM_LIBRARY_WRITE,
} from '../../../../../shared/constants/scopes';
import {
  PRIVATE_WORKSPACE,
  PRIVATE_WORKSPACE_DISPLAY_NAME,
  SHARED_WORKSPACE,
  SHARED_WORKSPACE_DISPLAY_NAME,
  TEAM_WORKSPACE,
  TEAM_WORKSPACE_DISPLAY_NAME,
} from '../../../../../shared/constants/workspaces';

import Transition from 'components/ui-components/transition';
import HierarchyGrid from 'components/videos-dashboard-ta/hierarchy-grid';

import styles from './styles.module.less';

const CONTAINER_CLASS = styles.moveModalContainer;

const SHOW_MODAL_CLASS = styles.showMoveModal;

const getOptions = scopes => {
  const options = [];

  if (scopes.hasTeamLibraryWriteScope) {
    options.push({
      value: TEAM_WORKSPACE,
      title: TEAM_WORKSPACE_DISPLAY_NAME,
      icon: <SvgUsers />,
    });
  }

  if (scopes.hasSharedLibraryWriteScope) {
    options.push({
      value: SHARED_WORKSPACE,
      title: SHARED_WORKSPACE_DISPLAY_NAME,
      icon: <SvgLock />,
    });
  }

  if (scopes.hasPersonalLibraryWriteScope) {
    options.push({
      value: PRIVATE_WORKSPACE,
      title: PRIVATE_WORKSPACE_DISPLAY_NAME,
      icon: <SvgUser />,
    });
  }

  return options;
};

const Loading = () => {
  return (
    <div className={styles.moveModalLoading}>
      <Loader size="large" color="grey6" />
      <Spacer top={2}>
        <Text color="bodyDimmed">Fetching Folders...</Text>
      </Spacer>
    </div>
  );
};

const NoContent = ({ selectedWorkspace }) => {
  if (selectedWorkspace === SHARED_WORKSPACE) {
    return (
      <div className={styles.moveModalNoSharedContent}>
        <Text color="bodyDimmed">
          No folders have been shared with you yet.
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.moveModalNoContent}>
      <Text color="bodyDimmed">
        There are no folders in this space. <br /> Create a new one to start
        organizing!
      </Text>
    </div>
  );
};

class MoveModal extends Component {
  focusNewFolderInput = null;
  hierarchyGridRef = null;
  newFolderInputRef = null;
  initialHierarchyGridTop = null;

  state = {
    selectedWorkspace: '',
    openedFolderIds: [],
  };

  componentDidMount() {
    const { workspace, videoOrganizationId, fetchSubFolders } = this.props;

    fetchSubFolders(workspace, videoOrganizationId);

    this.setState({
      selectedWorkspace: workspace,
    });
  }

  render() {
    const {
      checkedItems,
      currentFolderId,
      folderToMoveTo,
      show,
      showNewFolderInput,
      isFetchingInitialFolders,
      folders,
      workspace,
      scopes,
    } = this.props;
    const { selectedWorkspace, openedFolderIds } = this.state;
    const showGrid = folders.length > 0;
    const selectLabelText =
      workspace === selectedWorkspace ? 'Currently in:' : 'Move to:';

    if (!show) {
      return null;
    }

    let moveButtonDisabled = true;
    let name = null;

    if (checkedItems.length === 1) {
      name = checkedItems[0].name;
    } else if (_every(checkedItems, item => item.type === FOLDER)) {
      name = `${checkedItems.length} Folders`;
    } else if (_every(checkedItems, item => item.type === VIDEO)) {
      name = `${checkedItems.length} Videos`;
    } else {
      name = `${checkedItems.length} Items`;
    }

    if (
      !_isEmpty(folderToMoveTo) &&
      folderToMoveTo.id !== currentFolderId &&
      folderToMoveTo.id != null
    ) {
      moveButtonDisabled = false;
    }

    return (
      <Modal
        isOpen={true}
        hasDividers
        onCloseClick={this.props.onClickCancel}
        onBackgroundClick={this.props.onClickCancel}
        title={`Move ${name}`}
        mainButton={
          <Button
            disabled={moveButtonDisabled}
            onClick={this.move}
            variant="primary"
          >
            Move
          </Button>
        }
        secondaryButton={
          <Button onClick={this.props.onClickCancel}>Cancel</Button>
        }
        alternativeButton={
          <TextButton
            icon={<SvgAdd />}
            onClick={this.onClickNewFolder}
            isDisabled={
              isFetchingInitialFolders ||
              selectedWorkspace === SHARED_WORKSPACE ||
              (selectedWorkspace === TEAM_WORKSPACE &&
                !scopes.hasTeamLibraryWriteScope) ||
              (selectedWorkspace === PRIVATE_WORKSPACE &&
                !scopes.hasPersonalLibraryWriteScope)
            }
          >
            New Folder
          </TextButton>
        }
      >
        <Container maxWidth={28} paddingTop={1} paddingBottom={2}>
          <Spacer bottom={1}>
            <Text>{selectLabelText}</Text>
          </Spacer>
          <Select
            options={getOptions(scopes)}
            selectedOptionValue={this.state.selectedWorkspace}
            onChange={this.onChangeWorkspace}
          />
        </Container>
        <div className={CONTAINER_CLASS} onClick={this.exit}>
          <div className={styles.moveModal}>
            <div>
              {isFetchingInitialFolders && <Loading />}

              {!isFetchingInitialFolders && (
                <>
                  <TopLevelGridCell />

                  <div className={styles.grid}>
                    <Transition
                      alwaysRender={true}
                      show={this.props.showNewFolderInput}
                      onDidMount={() => this.playAnimation('showNewFolderName')}
                      onWillUnmount={() =>
                        this.playAnimation('hideNewFolderName')
                      }
                    >
                      <div
                        className={classnames(styles.moveNewFolderInputWrap, {
                          [styles.moveNewFolderInputShown]: showNewFolderInput,
                        })}
                      >
                        <Spacer right={2}>
                          <Icon icon={<SvgFolder />} />
                        </Spacer>
                        <input
                          type="text"
                          className={styles.moveNewFolderInput}
                          ref={this.refInputFolderName}
                          value={this.props.newFolderName}
                          onChange={this.onChangeNewFolderName}
                          onFocus={this.props.onFocusNewFolderName}
                          onBlur={this.props.onBlurNewFolderName}
                          onKeyUp={this.onKeyUpNewFolderName}
                        />
                      </div>
                    </Transition>

                    {!showGrid && (
                      <NoContent selectedWorkspace={selectedWorkspace} />
                    )}

                    {showGrid && (
                      <div
                        className={styles.hierarchyGridWrap}
                        ref={this.saveHierarychyGridRef}
                      >
                        <HierarchyGrid
                          selectedWorkspace={this.state.selectedWorkspace}
                          openedFolderIds={openedFolderIds}
                          openFolder={this.openFolder}
                          closeFolder={this.closeFolder}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  closeFolder = (e, id) => {
    e.stopPropagation();

    this.setState({
      openedFolderIds: this.state.openedFolderIds.filter(
        folderId => folderId !== id
      ),
    });
  };

  openFolder = async (e, id) => {
    e.stopPropagation();

    const { folders, fetchSubFolders } = this.props;

    const folder = folders.find(folder => folder.id === id);

    if (!folder.children) {
      await fetchSubFolders(id);
    }

    const { openedFolderIds } = this.state;

    if (openedFolderIds.some(folderId => folderId === id)) {
      return;
    }

    this.setState({ openedFolderIds: openedFolderIds.concat(id) });
  };

  UNSAFE_componentWillMount() {
    this._toggleShow(this.props.show);
  }

  componentWillUnmount() {
    this._toggleShow(this.props.show);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.show != nextProps.show) {
      this._toggleShow(nextProps.show);
    }

    if (!this.props.showNewFolderInput && nextProps.showNewFolderInput) {
      this.focusNewFolderInput = true;
    }
  }

  saveHierarychyGridRef = ref => {
    this.hierarchyGridRef = ref;

    if (ref) {
      // Save top position for later use in animation
      this.initialHierarchyGridTop = window.getComputedStyle(ref).top;
    }
  };

  playAnimation = name => {
    if (!this.newFolderInputRef) {
      return;
    }

    const elasticity = 200;

    if (name == 'showNewFolderName') {
      anime({
        targets: this.hierarchyGridRef,
        top: 0,
        elasticity,
      });

      return anime({
        targets: this.newFolderInputRef.parentNode,
        scaleY: [0, 1],
        opacity: [0, 1],
        elasticity,
      });
    }

    if (name == 'hideNewFolderName') {
      anime({
        targets: this.hierarchyGridRef,
        top: this.initialHierarchyGridTop,
        elasticity,
      });

      return anime({
        targets: this.newFolderInputRef.parentNode,
        scaleY: [1, 0],
        opacity: [1, 0],
        elasticity,
      });
    }
  };

  onClickNewFolder = () => {
    if (this.newFolderInputRef) {
      this.newFolderInputRef.select();
      this.newFolderInputRef
        .closest(`.${styles.moveModalContainer}`)
        .scrollTo(0, 0);
    }

    this.props.onClickNewFolder();
  };

  onKeyUpNewFolderName = event => {
    const { topLevelFolder } = this.props;

    if (KEY_ENTER.indexOf(event.key) > -1) {
      this.props.onEnteredNewFolderName(
        topLevelFolder.id,
        this.props.newFolderName
      );
    }

    if (KEY_ESCAPE.indexOf(event.key) > -1) {
      this.props.onCancelNewFolder();
    }
  };

  onChangeNewFolderName = event => {
    this.props.onChangeNewFolderName(event.target.value);
  };

  onChangeWorkspace = event => {
    this.setState({
      selectedWorkspace: event.value,
      openedFolderIds: [],
    });

    this.props.fetchSubFolders(event.value, this.props.videoOrganizationId);
  };

  refInputFolderName = ref => {
    this.newFolderInputRef = ref;

    if (this.focusNewFolderInput && ref) {
      this.focusNewFolderInput = false;
      ref.select();
      ref.closest(`.${styles.moveModalContainer}`).scrollTo(0, 0);
    }
  };

  _toggleShow(show) {
    // control body scrolling
    if (show) {
      document.body.classList.add(SHOW_MODAL_CLASS);
    } else {
      document.body.classList.remove(SHOW_MODAL_CLASS);
    }

    if (show) {
      Mousetrap.bind(ESCAPE, this.props.onHide);
    } else {
      Mousetrap.unbind(ESCAPE);
    }
  }

  exit = e => {
    if (e.target.className !== CONTAINER_CLASS) {
      return;
    }

    this.props.onHide();
  };

  move = () => {
    const { folderToMoveTo, isSharePage = false } = this.props;

    if (isSharePage) {
      this.props.onSharePageMove(
        folderToMoveTo.special_id || folderToMoveTo.id,
        folderToMoveTo.name,
        this.state.selectedWorkspace
      );
    } else {
      this.props.onMove(
        folderToMoveTo.special_id || folderToMoveTo.id,
        this.props.checkedItems,
        this.state.selectedWorkspace
      );
    }

    this.props.hideMoveModal();
  };
}

const mapStateToProps = (state, passedProps) => {
  const {
    checked_items: checkedItems,
    folder_id: currentFolderId,
    workspace: dashboardWorkspace,
  } = state.videos_dashboard;
  const videoWorkspace = state.share_video.video?.workspace;
  const videoOrganizationId = state.share_video.video?.organization_id;
  const { account_type: accountType } = state.user.current;
  const {
    folderToMoveTo,
    newFolderName,
    showNewFolderInput,
    isFetchingInitialFolders,
    folders,
    topLevelFolder,
    showMoveModal: show,
    items,
  } = state.moveModal;

  return {
    // Allow users of component to overwrite state with props
    currentFolderId: passedProps.currentFolderId || currentFolderId,
    showNewFolderInput,
    newFolderName,
    checkedItems: items && items.length ? items : checkedItems,
    folderToMoveTo,
    show,
    isFetchingInitialFolders,
    // the default workspace (library) should match the workspace assigned to the video if the user is on the share page,
    // otherwise we'll use the selected workspace from the dashboard.
    workspace: videoWorkspace || dashboardWorkspace,
    accountType,
    folders,
    topLevelFolder,
    scopes: {
      hasSharedLibraryWriteScope: hasScope(state, SHARED_LIBRARY_WRITE),
      hasTeamLibraryWriteScope: hasScope(state, TEAM_LIBRARY_WRITE),
      hasPersonalLibraryWriteScope: hasScope(state, PERSONAL_LIBRARY_WRITE),
    },
    videoOrganizationId,
  };
};

const mapDispatchToProps = dispatch => ({
  onFocusNewFolderName: () => dispatch(updateFolderToMoveTo(null)),
  onBlurNewFolderName: () => dispatch(createNewFolderHide()),
  onChangeNewFolderName: name => dispatch(createNewFolderUpdateName(name)),
  onClickNewFolder: () => dispatch(createNewFolderShow()),
  onCancelNewFolder: () => dispatch(createNewFolderHide()),
  onEnteredNewFolderName: (parentId, name) => {
    dispatch(createFolderCreator(parentId, name));
  },
  onClickCancel: () => dispatch(updateShowMoveModal(false)),
  onHide: () => dispatch(updateShowMoveModal(false)),
  onMove: (toFolderId, items, destinationWorkspace) => {
    dispatch(updatePastForMove());
    // The move modal cannot be used to archive items but it can be
    // used to restore from the archive, so we can safely pass a
    // hard-coded 'false' as the first param here to indicate
    // that videos should not be marked as archived in case this modal
    // is being used to restore from archive.
    dispatch(
      bulkMoveFoldersVideosCreator(
        false,
        toFolderId,
        items,
        destinationWorkspace
      )
    );
  },
  onSharePageMove: (newFolderId, newFolderName, destinationWorkspace) => {
    dispatch(
      moveVideoCreator(newFolderId, newFolderName, null, destinationWorkspace)
    );
  },
  clearMoveModalFolders: () => dispatch(clearMoveModalFolders()),
  fetchSubFolders: (folderId, orgId) =>
    dispatch(fetchSubFoldersCreator(folderId, orgId)),
  hideMoveModal: () => dispatch(updateShowMoveModal(false)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MoveModal);
